import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { LocalizationModal } from '@/components/admin/localization-modal/LocalizationModal';
import { TranslationControls } from '@/components/admin/translation-controls/TranslationControls';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import { HistorySectionDto } from '@/types/common/history-sections';
import { LocalizationLanguage } from '@/types/common/language';
import { ContentType } from '@/types/common/section-contents';
import { SectionMode, SectionTemplate } from '@/types/common/sections';
import { useTranslateHistorySection } from '@/hooks/admin/use-translate-history-section/useTranslateHistorySection';
import {
    TranslateHistorySectionForm,
    TranslateHistorySectionFormRef,
    TranslateHistorySectionFormValues,
} from './TranslateHistorySectionForm';
import { renderHistorySection } from '@/utils/functions/render-history-section';
import { getHistorySectionData, getOrderedHistoryContentsByType } from '@/utils/functions/history-section-data';
import styles from './TranslateHistoryModal.module.scss';

export type HistoryImagePosition = 'top' | 'bottom' | 'right';

export const HISTORY_TEMPLATE_IMAGE_POSITION: Partial<Record<SectionTemplate, HistoryImagePosition>> = {
    [SectionTemplate.SingleImageTop]: 'top',
    [SectionTemplate.SingleImageRight]: 'right',
    [SectionTemplate.SingleImageBottom]: 'bottom',
    [SectionTemplate.DualImagesBottom]: 'bottom',
    [SectionTemplate.TripleImagesBottom]: 'bottom',
    [SectionTemplate.QuadImagesBottom]: 'bottom',
};

export const getHistoryImagePosition = (template: SectionTemplate): HistoryImagePosition =>
    HISTORY_TEMPLATE_IMAGE_POSITION[template] ?? 'bottom';

const getInitialData = (section: HistorySectionDto, languageId?: number): TranslateHistorySectionFormValues | null => {
    if (!languageId) return null;

    const titleContent = getOrderedHistoryContentsByType(section.contents, ContentType.Title)[0];
    const descriptionContent = getOrderedHistoryContentsByType(section.contents, ContentType.Description)[0];

    const title = titleContent?.localizations?.find((l) => l.localizationInfoDto.id === languageId)?.title ?? '';
    const description =
        descriptionContent?.localizations?.find((l) => l.localizationInfoDto.id === languageId)?.description ?? '';

    return title || description ? { title, description } : null;
};

interface TranslateHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    sections: HistorySectionDto[];
    languages: LocalizationLanguage[];
    onSaved: (updatedSections: HistorySectionDto[]) => void;
}

interface SectionTranslationState {
    isFormValid: boolean;
    isDirty: boolean;
    formRef: React.RefObject<TranslateHistorySectionFormRef | null>;
    section: HistorySectionDto;
}

export const TranslateHistoryModal = ({
    isOpen,
    onClose,
    sections,
    languages,
    onSaved,
}: TranslateHistoryModalProps) => {
    const [language, setLanguage] = useState<LocalizationLanguage | null>(languages[0] ?? null);
    const [sectionStates, setSectionStates] = useState<SectionTranslationState[]>(() =>
        sections.map((section) => ({
            isFormValid: true,
            isDirty: false,
            formRef: { current: null } as React.RefObject<TranslateHistorySectionFormRef | null>,
            section,
        })),
    );

    const { translateSections, isSubmitting } = useTranslateHistorySection({
        sections,
        language,
        onSuccess: (updatedSections) => {
            onSaved(updatedSections);
            onClose();
        },
    });

    const isFormValid = sectionStates.every((s) => s.isFormValid);
    const isDirty = sectionStates.some((s) => s.isDirty);

    const updateSectionState = useCallback(
        (index: number, updates: Partial<Pick<SectionTranslationState, 'isFormValid' | 'isDirty'>>) => {
            setSectionStates((prev) => {
                const target = prev[index];
                if (!target) return prev;

                let hasChanges = false;
                for (const key in updates) {
                    const typedKey = key as keyof typeof updates;
                    if (target[typedKey] !== updates[typedKey]) {
                        hasChanges = true;
                        break;
                    }
                }

                if (!hasChanges) return prev;

                return prev.map((s, i) => (i === index ? { ...s, ...updates } : s));
            });
        },
        [],
    );

    const checkIsDirty = () => isDirty;

    const handleSave = async () => {
        for (const { formRef } of sectionStates) {
            if (formRef.current && !formRef.current.isValid()) return;
        }

        const dataMap = [];
        for (const { formRef, section } of sectionStates) {
            if (formRef.current && section.id !== undefined) {
                const data = formRef.current.getValues?.();
                if (data !== undefined) {
                    dataMap.push({ sectionId: section.id, data });
                }
            }
        }

        await translateSections(dataMap);
    };

    const isEditMode = useMemo(() => {
        return sections.some((section) => getInitialData(section, language?.id) !== null);
    }, [sections, language?.id]);

    return (
        <LocalizationModal
            isOpen={isOpen}
            onClose={onClose}
            title={
                isEditMode
                    ? COMMON_TEXT_ADMIN.LOCALIZATION.FORM.TITLE.UPDATE_TRANSLATION
                    : COMMON_TEXT_ADMIN.LOCALIZATION.FORM.TITLE.ADD_TRANSLATION
            }
            onSave={handleSave}
            isSubmitting={isSubmitting}
            isFormValid={isFormValid}
            checkIsDirty={checkIsDirty}
            isDirty={isDirty}
            maxWidth="1200px"
        >
            <TranslationControls
                selectedLanguage={language}
                isSubmitting={isSubmitting}
                languages={languages}
                onLanguageChange={setLanguage}
            />

            <div className={styles['sections-list']}>
                {sectionStates.map((state, index) => {
                    const { section, formRef } = state;

                    return (
                        <SectionTranslationRow
                            key={`${section.id ?? index}-${language?.id}`}
                            index={index}
                            formRef={formRef}
                            section={section}
                            languageId={language?.id}
                            updateSectionState={updateSectionState}
                        />
                    );
                })}
            </div>
        </LocalizationModal>
    );
};

interface SectionTranslationRowProps {
    index: number;
    formRef: React.RefObject<TranslateHistorySectionFormRef | null>;
    section: HistorySectionDto;
    languageId?: number;
    updateSectionState: (
        index: number,
        updates: Partial<Pick<SectionTranslationState, 'isFormValid' | 'isDirty'>>,
    ) => void;
}

const SectionTranslationRow = ({
    index,
    formRef,
    section,
    languageId,
    updateSectionState,
}: SectionTranslationRowProps) => {
    const initialData = useMemo(() => getInitialData(section, languageId), [section, languageId]);

    const handleValidationChange = useCallback(
        (isValid: boolean) => updateSectionState(index, { isFormValid: isValid }),
        [index, updateSectionState],
    );

    const handleDirtyChange = useCallback(
        (dirty: boolean) => updateSectionState(index, { isDirty: dirty }),
        [index, updateSectionState],
    );

    const hasImage = section.contents.some((c) => c.contentType === ContentType.Image);
    const imagesPreview = hasImage
        ? renderHistorySection({
              templateId: section.template,
              data: {
                  title: '',
                  description: '',
                  images: getHistorySectionData(section).images,
              },
              mode: SectionMode.View,
              validationResetKey: 0,
          })
        : null;
    const imagePosition = getHistoryImagePosition(section.template);
    const previewNode = imagesPreview ? (
        <div className={styles['section-preview']} data-testid="history-section-images-preview">
            {imagesPreview}
        </div>
    ) : null;

    return (
        <div
            className={cn(styles['section-row'], styles[`section-row--${imagePosition}`])}
            data-testid="translate-section-row"
            data-image-position={imagePosition}
        >
            {imagePosition === 'top' && previewNode}
            <TranslateHistorySectionForm
                ref={formRef}
                initialData={initialData}
                onSubmit={() => {}}
                onValidationChange={handleValidationChange}
                onDirtyChange={handleDirtyChange}
                stacked={imagePosition === 'right'}
            />
            {imagePosition !== 'top' && previewNode}
        </div>
    );
};
